export type StoredAuthSession = {
  token: string;
  expiresAt: string;
};

export type StoredAuthIdentity = {
  username: string;
  userId?: string;
  email?: string;
  publicKey: string;
  privateKeyJwk: JsonWebKey;
};

export const AUTH_USER_STORAGE_KEY = "nebula.auth.user";
export const AUTH_SESSION_STORAGE_KEY = "nebula.auth.session";
export const AUTH_IDENTITIES_STORAGE_KEY = "nebula.auth.identities.v1";
export const AUTH_IDENTITY_STORAGE_KEY = "nebula.auth.identity";

function normalizeStoredIdentity(
  raw: Partial<StoredAuthIdentity> | null | undefined,
): StoredAuthIdentity | null {
  const username = String(raw?.username ?? "")
    .trim()
    .toLowerCase();
  const userId = String(raw?.userId ?? "").trim();
  const email = String(raw?.email ?? "")
    .trim()
    .toLowerCase();
  const publicKey = String(raw?.publicKey ?? "").trim();
  const privateKeyJwk = raw?.privateKeyJwk;
  if (!username || !publicKey || !privateKeyJwk || typeof privateKeyJwk !== "object") return null;
  return {
    username,
    userId: userId || undefined,
    email: email || undefined,
    publicKey,
    privateKeyJwk,
  };
}

function identityMatchIndex(
  identities: StoredAuthIdentity[],
  candidate: StoredAuthIdentity,
): number {
  const candidateUserId = String(candidate.userId || "").trim();
  if (candidateUserId) {
    const byUserId = identities.findIndex(
      (identity) => String(identity.userId || "").trim() === candidateUserId,
    );
    if (byUserId >= 0) return byUserId;
  }
  const candidateUsername = String(candidate.username || "")
    .trim()
    .toLowerCase();
  return identities.findIndex(
    (identity) =>
      String(identity.username || "")
        .trim()
        .toLowerCase() === candidateUsername,
  );
}

function dedupeIdentities(identities: StoredAuthIdentity[]): StoredAuthIdentity[] {
  const next: StoredAuthIdentity[] = [];
  for (const identity of identities) {
    const normalized = normalizeStoredIdentity(identity);
    if (!normalized) continue;
    const existingIndex = identityMatchIndex(next, normalized);
    if (existingIndex >= 0) next[existingIndex] = normalized;
    else next.push(normalized);
  }
  return next;
}

function readStoredUserHint(): { id: string; username: string } {
  if (typeof window === "undefined") return { id: "", username: "" };
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return { id: "", username: "" };
    const parsed = JSON.parse(raw) as { id?: string; username?: string };
    return {
      id: String(parsed?.id || "").trim(),
      username: String(parsed?.username || "")
        .trim()
        .toLowerCase(),
    };
  } catch {
    return { id: "", username: "" };
  }
}

export function readStoredAuthSession(): StoredAuthSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAuthSession>;
    const token = String(parsed.token ?? "").trim();
    const expiresAt = String(parsed.expiresAt ?? "").trim();
    if (!token || !expiresAt) return null;
    return { token, expiresAt };
  } catch {
    return null;
  }
}

export function writeStoredAuthSession(session: StoredAuthSession | null) {
  if (typeof window === "undefined") return;
  if (!session) {
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function readStoredAuthIdentities(): StoredAuthIdentity[] {
  if (typeof window === "undefined") return [];
  try {
    const rawMulti = window.localStorage.getItem(AUTH_IDENTITIES_STORAGE_KEY);
    if (rawMulti) {
      const parsed = JSON.parse(rawMulti) as Array<Partial<StoredAuthIdentity>>;
      if (Array.isArray(parsed)) return dedupeIdentities(parsed as StoredAuthIdentity[]);
    }
  } catch {
    // fallback to legacy key below
  }

  try {
    const rawLegacy = window.localStorage.getItem(AUTH_IDENTITY_STORAGE_KEY);
    if (!rawLegacy) return [];
    const parsedLegacy = JSON.parse(rawLegacy) as Partial<StoredAuthIdentity>;
    const normalized = normalizeStoredIdentity(parsedLegacy);
    return normalized ? [normalized] : [];
  } catch {
    return [];
  }
}

export function writeStoredAuthIdentities(identities: StoredAuthIdentity[]) {
  if (typeof window === "undefined") return;
  const normalized = dedupeIdentities(identities);
  if (normalized.length === 0) {
    window.localStorage.removeItem(AUTH_IDENTITIES_STORAGE_KEY);
    window.localStorage.removeItem(AUTH_IDENTITY_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(AUTH_IDENTITIES_STORAGE_KEY, JSON.stringify(normalized));
  // Keep legacy single-key slot for older clients.
  window.localStorage.setItem(AUTH_IDENTITY_STORAGE_KEY, JSON.stringify(normalized[0]));
}

export function upsertStoredAuthIdentity(identity: StoredAuthIdentity) {
  const normalized = normalizeStoredIdentity(identity);
  if (!normalized) return;
  const identities = readStoredAuthIdentities();
  const index = identityMatchIndex(identities, normalized);
  if (index >= 0) identities[index] = normalized;
  else identities.push(normalized);
  writeStoredAuthIdentities(identities);
}

export function findStoredAuthIdentityByUserID(userId: string): StoredAuthIdentity | null {
  const key = String(userId || "").trim();
  if (!key) return null;
  const identities = readStoredAuthIdentities();
  return identities.find((identity) => String(identity.userId || "").trim() === key) || null;
}

export function findStoredAuthIdentityByUsername(username: string): StoredAuthIdentity | null {
  const key = String(username || "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  const identities = readStoredAuthIdentities();
  return (
    identities.find(
      (identity) =>
        String(identity.username || "")
          .trim()
          .toLowerCase() === key,
    ) || null
  );
}

export function readStoredAuthIdentity(): StoredAuthIdentity | null {
  const identities = readStoredAuthIdentities();
  if (identities.length === 0) return null;
  const hint = readStoredUserHint();
  if (hint.id) {
    const byUserID = identities.find(
      (identity) => String(identity.userId || "").trim() === hint.id,
    );
    if (byUserID) return byUserID;
  }
  if (hint.username) {
    const byUsername = identities.find(
      (identity) =>
        String(identity.username || "")
          .trim()
          .toLowerCase() === hint.username,
    );
    if (byUsername) return byUsername;
  }
  return identities[0];
}

export function writeStoredAuthIdentity(identity: StoredAuthIdentity | null) {
  if (!identity) {
    writeStoredAuthIdentities([]);
    return;
  }
  upsertStoredAuthIdentity(identity);
}
